import { forwardRef } from "react";
import { cn } from "../../lib/utils";

/**
 * PremiumIcon — the single source of truth for how icons render across
 * RivoNes. Wraps any lucide-react icon (or any SVG component) in a
 * consistent container with subtle depth, motion, and Rivones brand
 * gradients. Inspired by the icon language of Stripe, Linear, Revolut,
 * and Apple Wallet — depth via light + shadow, not skeuomorphic 3D.
 *
 * Usage:
 *   <PremiumIcon icon={Shield} variant="gradient" size="md" />
 *   <PremiumIcon icon={Calendar} variant="glass" size="lg" tone="blue" />
 *
 * Variants:
 *   • plain     — no container; polished stroke + hover motion only
 *   • glass     — frosted-glass circle (backdrop-blur, translucent bg)
 *   • gradient  — filled blue gradient tile with soft inner glow
 *   • elevated  — white tile with layered soft shadow + hairline border
 *
 * Tones:
 *   • blue      — Rivones primary (default for CTAs + verification)
 *   • terracotta — brand accent (host/rewards)
 *   • emerald   — success / verified
 *   • amber     — warnings / rewards
 *   • neutral   — passive states
 */

const SIZE_MAP = {
  xs: { tile: "w-7 h-7", icon: "w-3.5 h-3.5", radius: "rounded-lg" },
  sm: { tile: "w-9 h-9", icon: "w-4 h-4", radius: "rounded-xl" },
  md: { tile: "w-11 h-11", icon: "w-5 h-5", radius: "rounded-xl" },
  lg: { tile: "w-14 h-14", icon: "w-6 h-6", radius: "rounded-2xl" },
  xl: { tile: "w-16 h-16", icon: "w-7 h-7", radius: "rounded-2xl" },
};

const TONE_MAP = {
  blue: {
    gradient: "bg-[linear-gradient(135deg,#2563EB_0%,#4F8EF7_100%)] text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.55)]",
    glass:    "bg-blue-500/10 text-blue-600 backdrop-blur-md ring-1 ring-blue-500/15",
    elevated: "bg-white text-blue-600 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_20px_-10px_rgba(37,99,235,0.35)] ring-1 ring-blue-100",
    plain:    "text-blue-600",
  },
  terracotta: {
    gradient: "bg-[linear-gradient(135deg,#8B3A2B_0%,#C2564A_100%)] text-white shadow-[0_6px_16px_-6px_rgba(139,58,43,0.55)]",
    glass:    "bg-terracotta/10 text-terracotta backdrop-blur-md ring-1 ring-terracotta/15",
    elevated: "bg-white text-terracotta shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_20px_-10px_rgba(139,58,43,0.35)] ring-1 ring-terracotta/15",
    plain:    "text-terracotta",
  },
  emerald: {
    gradient: "bg-[linear-gradient(135deg,#059669_0%,#34D399_100%)] text-white shadow-[0_6px_16px_-6px_rgba(5,150,105,0.55)]",
    glass:    "bg-emerald-500/10 text-emerald-700 backdrop-blur-md ring-1 ring-emerald-500/15",
    elevated: "bg-white text-emerald-600 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_20px_-10px_rgba(5,150,105,0.35)] ring-1 ring-emerald-100",
    plain:    "text-emerald-600",
  },
  amber: {
    gradient: "bg-[linear-gradient(135deg,#D97706_0%,#FBBF24_100%)] text-white shadow-[0_6px_16px_-6px_rgba(217,119,6,0.55)]",
    glass:    "bg-amber-500/10 text-amber-700 backdrop-blur-md ring-1 ring-amber-500/15",
    elevated: "bg-white text-amber-600 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_20px_-10px_rgba(217,119,6,0.35)] ring-1 ring-amber-100",
    plain:    "text-amber-600",
  },
  neutral: {
    gradient: "bg-[linear-gradient(135deg,#334155_0%,#64748B_100%)] text-white shadow-[0_6px_16px_-6px_rgba(51,65,85,0.45)]",
    glass:    "bg-neutral-500/10 text-neutral-700 backdrop-blur-md ring-1 ring-neutral-500/10",
    elevated: "bg-white text-neutral-700 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_6px_16px_-10px_rgba(15,23,42,0.4)] ring-1 ring-neutral-200",
    plain:    "text-neutral-600",
  },
};

const PremiumIcon = forwardRef(function PremiumIcon(
  { icon: IconCmp, variant = "plain", tone = "blue", size = "md", className, iconClassName, animate = true, ariaLabel, ...rest },
  ref,
) {
  const s = SIZE_MAP[size] || SIZE_MAP.md;
  const toneStyles = TONE_MAP[tone] || TONE_MAP.blue;
  const containerStyle = toneStyles[variant] || toneStyles.plain;
  const strokeWidth = variant === "gradient" ? 2 : 1.75;

  const motion = animate
    ? "transition-all duration-200 ease-out hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
    : "";

  if (variant === "plain") {
    return (
      <IconCmp
        ref={ref}
        className={cn(s.icon, containerStyle, motion, iconClassName, className)}
        strokeWidth={strokeWidth}
        aria-label={ariaLabel}
        {...rest}
      />
    );
  }

  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center flex-shrink-0",
        s.tile,
        s.radius,
        containerStyle,
        motion,
        className,
      )}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      {...rest}
    >
      <IconCmp className={cn(s.icon, iconClassName)} strokeWidth={strokeWidth} />
    </span>
  );
});

export default PremiumIcon;
